import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Nav from "@/components/Nav";
import GeoMap from "@/components/GeoMap";
import { api, formatIDR, fileUrl } from "@/lib/api";
import { ArrowRight, Lightning, Trophy, MapPin, Certificate, Barcode, CheckCircle, InstagramLogo, TiktokLogo, WhatsappLogo, YoutubeLogo, X } from "@phosphor-icons/react";

const HERO_IMG = "https://images.unsplash.com/photo-1581889470536-467bdbe30cd0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHw0fHxtYXJhdGhvbiUyMHJ1bm5lciUyMHN1bnNldHxlbnwwfHx8fDE3ODc4MTAxODJ8MA&ixlib=rb-4.1.0&q=85";
const REG_IMG = "https://images.unsplash.com/photo-1526676317768-d9b14f15615a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHw0fHxydW5uaW5nJTIwc2hvZXMlMjB0cmFja3xlbnwwfHx8fDE3ODc4MTAxODJ8MA&ixlib=rb-4.1.0&q=85";

function Slideshow({ images, alt, testid }) {
  const [idx, setIdx] = useState(0);
  const [lightbox, setLightbox] = useState(false);
  useEffect(() => {
    if (images.length <= 1) return undefined;
    const t = setInterval(() => setIdx((i) => (i + 1) % images.length), 3000);
    return () => clearInterval(t);
  }, [images.length]);
  const prev = (e) => { e.stopPropagation(); setIdx((i) => (i - 1 + images.length) % images.length); };
  const next = (e) => { e.stopPropagation(); setIdx((i) => (i + 1) % images.length); };
  return (
    <>
      <div
        className="relative w-full aspect-[4/3] border border-[#27272A] overflow-hidden cursor-zoom-in"
        data-testid={testid}
        onClick={() => setLightbox(true)}
      >
        {images.map((img, i) => (
          <img
            key={i}
            src={fileUrl(img)}
            alt={alt}
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${i === idx ? "opacity-100" : "opacity-0"}`}
          />
        ))}
        {images.length > 1 && (
          <div className="absolute bottom-2 left-0 right-0 flex justify-center gap-1.5 z-10">
            {images.map((_, i) => (
              <button
                key={i}
                onClick={(e) => { e.stopPropagation(); setIdx(i); }}
                className={`w-2 h-2 transition-colors ${i === idx ? "bg-[#FF3B30]" : "bg-white/30 hover:bg-white/60"}`}
                data-testid={`${testid}-dot-${i}`}
                aria-label={`Slide ${i + 1}`}
              />
            ))}
          </div>
        )}
      </div>

      {lightbox && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 md:p-12"
          onClick={() => setLightbox(false)}
          data-testid={`${testid}-lightbox`}
        >
          <button
            onClick={() => setLightbox(false)}
            className="absolute top-4 right-4 text-white/70 hover:text-[#FF3B30] text-3xl font-display px-3 py-1 z-10"
            data-testid={`${testid}-close`}
            aria-label="Close"
          >
            ×
          </button>
          {images.length > 1 && (
            <button
              onClick={prev}
              className="absolute left-3 md:left-8 top-1/2 -translate-y-1/2 text-white/70 hover:text-[#FF3B30] text-4xl font-display px-3 py-2 z-10"
              data-testid={`${testid}-prev`}
              aria-label="Previous"
            >
              ‹
            </button>
          )}
          <img
            src={fileUrl(images[idx])}
            alt={alt}
            className="max-w-full max-h-full object-contain border border-[#27272A]"
            onClick={(e) => e.stopPropagation()}
            data-testid={`${testid}-lightbox-img`}
          />
          {images.length > 1 && (
            <button
              onClick={next}
              className="absolute right-3 md:right-8 top-1/2 -translate-y-1/2 text-white/70 hover:text-[#FF3B30] text-4xl font-display px-3 py-2 z-10"
              data-testid={`${testid}-next`}
              aria-label="Next"
            >
              ›
            </button>
          )}
          {images.length > 1 && (
            <div className="absolute bottom-5 left-0 right-0 flex justify-center gap-2">
              {images.map((_, i) => (
                <button
                  key={i}
                  onClick={(e) => { e.stopPropagation(); setIdx(i); }}
                  className={`w-2.5 h-2.5 transition-colors ${i === idx ? "bg-[#FF3B30]" : "bg-white/30 hover:bg-white/60"}`}
                  data-testid={`${testid}-lb-dot-${i}`}
                  aria-label={`Slide ${i + 1}`}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </>
  );
}

export default function Home() {
  const [categories, setCategories] = useState([]);
  const [config, setConfig] = useState(null);
  const [stats, setStats] = useState(null);
  const [showTerms, setShowTerms] = useState(false);
  const [events, setEvents] = useState([]);

  useEffect(() => {
    api.get("/events").then((r) => {
      const first = (r.data || []).find((e) => e.is_active) || (r.data || [])[0];
      if (first) api.get(`/categories?event_id=${first.id}`).then((res) => setCategories(res.data)).catch(() => {});
    }).catch(() => {});
    api.get("/events").then((r) => setEvents(r.data || [])).catch(() => {});
    api.get("/config").then((r) => setConfig(r.data)).catch(() => {});
    const loadStats = () => api.get("/stats/public").then((r) => setStats(r.data)).catch(() => {});
    loadStats();
    const t = setInterval(loadStats, 30000);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="min-h-screen">
      <Nav />

      {/* Hero */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover object-center" />
          <div className="absolute inset-0 bg-black/60" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 md:px-8 py-24 md:py-36 grid md:grid-cols-12 gap-8">
          <div className="md:col-span-8">
            <div className="inline-flex items-center gap-2 border border-[#FF3B30] px-3 py-1 text-[10px] tracking-[0.3em] font-display text-[#FF3B30]">
              <Lightning size={12} weight="fill" /> REGISTRATION OPEN · 2026 EDITION
            </div>
            {config?.event_name ? (
              <h1 className="font-display text-5xl sm:text-6xl lg:text-8xl leading-[0.9] mt-6 tracking-tight">
                {config.event_name.split(" ").slice(0, -1).join(" ") || config.event_name}
                <br />
                <span className="text-[#FF3B30]">{config.event_name.split(" ").slice(-1)[0]}</span>
              </h1>
            ) : (
              <div className="mt-6 h-[180px] lg:h-[320px]" aria-hidden="true" />
            )}
            <p className="mt-6 max-w-xl text-white/70 text-base leading-relaxed" data-testid="hero-description">
              {config ? (config.event_description || "Daftar, transfer, dapatkan e-BIB otomatis, submit hasil larimu dari mana saja, dan unduh e-sertifikat resmi. Semua dalam satu platform.") : " "}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/register" className="btn-blaze inline-flex items-center gap-2" data-testid="hero-register-btn">
                REGISTER NOW <ArrowRight size={18} weight="bold" />
              </Link>
              <Link to="/leaderboard" className="btn-outline inline-flex items-center gap-2" data-testid="hero-leaderboard-btn">
                VIEW LEADERBOARD <Trophy size={18} weight="bold" />
              </Link>
            </div>
          </div>
          <div className="md:col-span-4 md:pl-8 border-l border-white/10 hidden md:flex flex-col justify-end">
            <div className="text-[10px] tracking-[0.3em] text-white/50 font-display">RUN WHERE YOU ARE</div>
            <div className="text-4xl font-display mt-2">ANYWHERE.<br />ANYTIME.</div>
            <div className="mt-6 text-xs text-white/50 tracking-widest">Timing recorded via your favorite tracking app.</div>
          </div>
        </div>
        {/* Marquee */}
        <div className="relative border-t border-white/10 bg-[#0A0A0A] py-3 overflow-hidden">
          <div className="marquee-track font-display text-sm tracking-[0.4em] text-white/40">
            {Array.from({ length: 2 }).map((_, i) => (
              <div key={i} className="flex gap-12">
                <span>RUN · SUBMIT · CERTIFY</span>
                <span className="text-[#FF3B30]">/</span>
                <span>5K · 10K · 21K</span>
                <span className="text-[#FF3B30]">/</span>
                <span>OFFICIAL E-BIB</span>
                <span className="text-[#FF3B30]">/</span>
                <span>LEADERBOARD LIVE</span>
                <span className="text-[#FF3B30]">/</span>
              </div>
            ))}
          </div>
        </div>
        {/* Live stats */}
        {stats && config?.show_live_stats !== false && (
          <div className="relative border-t border-white/10 bg-[#0A0A0A]" data-testid="live-stats">
            <div className="max-w-7xl mx-auto px-4 md:px-8 grid grid-cols-2 divide-x divide-white/10">
              <div className="py-8 pr-8">
                <div className="text-[10px] tracking-[0.3em] text-white/50 font-display flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-[#FF3B30] animate-pulse" /> LIVE · TOTAL PENDAFTAR
                </div>
                <div className="font-display text-5xl md:text-7xl mt-2 font-mono-num" data-testid="stat-registrants">{stats.total_registrations}</div>
              </div>
              <div className="py-8 pl-8">
                <div className="text-[10px] tracking-[0.3em] text-white/50 font-display flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" /> LIVE · FINISHERS
                </div>
                <div className="font-display text-5xl md:text-7xl mt-2 font-mono-num text-[#FF3B30]" data-testid="stat-finishers">{stats.total_finishers}</div>
              </div>
            </div>
          </div>
        )}
      </section>

      <GeoMap events={events} />

      {/* How it works */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-20" data-testid="how-section">
        <div className="grid md:grid-cols-12 gap-8">
          <div className="md:col-span-4">
            <div className="text-[10px] tracking-[0.3em] text-[#FF3B30] font-display">04 STEPS</div>
            <h2 className="font-display text-5xl mt-4">HOW IT<br />WORKS</h2>
          </div>
          <div className="md:col-span-8 grid sm:grid-cols-2 gap-4">
            {[
              { icon: Lightning, t: "01 · REGISTER", d: "Isi data diri & pilih kategori larimu." },
              { icon: CheckCircle, t: "02 · TRANSFER", d: "Kirim pembayaran ke rekening panitia lalu upload buktinya." },
              { icon: Barcode, t: "03 · GET E-BIB", d: "E-BIB dengan nomor start otomatis terbit di dashboard." },
              { icon: Certificate, t: "04 · GET CERT", d: "Submit hasil, tim kami verifikasi, sertifikat diterbitkan." },
            ].map((s, i) => (
              <div key={i} className="card-flat p-6" data-testid={`step-card-${i + 1}`}>
                <s.icon size={28} color="#FF3B30" weight="duotone" />
                <div className="mt-4 font-display tracking-widest text-lg">{s.t}</div>
                <div className="mt-2 text-sm text-white/60">{s.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-20 border-t border-white/10" data-testid="categories-section">
        <div className="grid md:grid-cols-12 gap-8 items-end">
          <div className="md:col-span-6">
            <div className="text-[10px] tracking-[0.3em] text-[#FF3B30] font-display">PICK YOUR DISTANCE</div>
            <h2 className="font-display text-5xl mt-4">CATEGORIES</h2>
          </div>
          <div className="md:col-span-6 text-white/60 text-sm">
            Every runner picks their own battlefield. Choose your distance, pay the entry, and lace up.
          </div>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-10">
          {categories.filter((c) => c.is_active).map((c) => (
            <div key={c.id} className="card-flat p-8 hover:border-[#FF3B30] transition-colors" data-testid={`cat-card-${c.name}`}>
              <div className="flex items-baseline justify-between">
                <div className="font-display text-6xl text-[#FF3B30]">{c.name}</div>
                <MapPin size={22} color="#A1A1AA" />
              </div>
              <div className="mt-4 text-xs text-white/50 tracking-widest uppercase">DISTANCE</div>
              <div className="font-display text-2xl font-mono-num">{c.distance_km} KM</div>
              <div className="mt-4 text-xs text-white/50 tracking-widest uppercase">ENTRY FEE</div>
              <div className="font-display text-2xl">{formatIDR(c.price_idr)}</div>
              {c.description && <div className="mt-4 text-sm text-white/60">{c.description}</div>}
            </div>
          ))}
        </div>
        <div className="mt-10">
          <Link to="/register" className="btn-blaze inline-flex items-center gap-2" data-testid="categories-cta">
            REGISTER FOR EVENT <ArrowRight size={18} weight="bold" />
          </Link>
        </div>
      </section>

      {/* Active Events */}
      {events.filter((e) => e.is_active).length > 0 && (
        <section className="max-w-7xl mx-auto px-4 md:px-8 py-20 border-t border-white/10" data-testid="events-section">
          <div className="grid md:grid-cols-12 gap-8 items-end">
            <div className="md:col-span-6">
              <div className="text-[10px] tracking-[0.3em] text-[#FF3B30] font-display">CHOOSE YOUR EVENT</div>
              <h2 className="font-display text-5xl mt-4">ACTIVE EVENTS</h2>
            </div>
            <div className="md:col-span-6 text-white/60 text-sm">
              Setiap event punya jersey, medali, e-BIB, dan sertifikat dengan desainnya sendiri.
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-6 mt-10">
            {events.filter((e) => e.is_active).map((ev) => (
              <div key={ev.id} className="card-flat p-8" data-testid={`event-card-${ev.id}`}>
                <div className="font-display text-3xl tracking-wide">{ev.name}</div>
                {ev.description && <p className="mt-3 text-sm text-white/60 leading-relaxed">{ev.description}</p>}
                {((ev.jersey_images || []).length > 0 || (ev.medal_images || []).length > 0) && (
                  <div className="grid grid-cols-2 gap-3 mt-6">
                    {(ev.jersey_images || []).length > 0 && (
                      <div>
                        <Slideshow images={ev.jersey_images} alt={`${ev.name} Jersey`} testid={`ev-jersey-${ev.id}`} />
                        <div className="mt-2 text-[10px] tracking-widest text-white/50 font-display">EVENT JERSEY</div>
                      </div>
                    )}
                    {(ev.medal_images || []).length > 0 && (
                      <div>
                        <Slideshow images={ev.medal_images} alt={`${ev.name} Medal`} testid={`ev-medal-${ev.id}`} />
                        <div className="mt-2 text-[10px] tracking-widest text-white/50 font-display">FINISHER MEDAL</div>
                      </div>
                    )}
                  </div>
                )}
                <Link to={`/register?event=${ev.id}`} className="btn-blaze inline-flex items-center gap-2 mt-6" data-testid={`event-register-${ev.id}`}>
                  REGISTER <ArrowRight size={16} weight="bold" />
                </Link>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Footer band */}
      <section className="relative border-t border-white/10" data-testid="footer-band">
        <div className="absolute inset-0">
          <img src={REG_IMG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/70" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 md:px-8 py-24 grid md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-8">
            <h3 className="font-display text-4xl md:text-6xl">READY TO<br />COMMIT?</h3>
            <p className="mt-4 text-white/70 max-w-xl">Registrasi hanya butuh 2 menit. E-BIB, sertifikat, dan leaderboard — semuanya otomatis.</p>
          </div>
          <div className="md:col-span-4 flex flex-col gap-3">
            <Link to="/register" className="btn-blaze text-center" data-testid="footer-register-btn">START REGISTRATION</Link>
            <Link to="/check" className="btn-outline text-center" data-testid="footer-check-btn">CHECK MY STATUS</Link>
          </div>
        </div>
      </section>
      <footer className="border-t border-white/10 py-8 text-center text-xs text-white/40 tracking-widest">
        {(config?.social_instagram || config?.social_tiktok || config?.social_whatsapp || config?.social_youtube) && (
          <div className="flex items-center justify-center gap-5 mb-5" data-testid="footer-socials">
            {config.social_instagram && <a href={config.social_instagram} target="_blank" rel="noreferrer" aria-label="Instagram Allied" className="text-white/50 hover:text-[#FF3B30] transition-colors" data-testid="social-instagram"><InstagramLogo size={22} /></a>}
            {config.social_tiktok && <a href={config.social_tiktok} target="_blank" rel="noreferrer" aria-label="TikTok Allied" className="text-white/50 hover:text-[#FF3B30] transition-colors" data-testid="social-tiktok"><TiktokLogo size={22} /></a>}
            {config.social_whatsapp && <a href={config.social_whatsapp} target="_blank" rel="noreferrer" aria-label="WhatsApp Allied" className="text-white/50 hover:text-[#FF3B30] transition-colors" data-testid="social-whatsapp"><WhatsappLogo size={22} /></a>}
            {config.social_youtube && <a href={config.social_youtube} target="_blank" rel="noreferrer" aria-label="YouTube Allied" className="text-white/50 hover:text-[#FF3B30] transition-colors" data-testid="social-youtube"><YoutubeLogo size={22} /></a>}
          </div>
        )}
        {config?.terms_text && (
          <button onClick={() => setShowTerms(true)} className="block mx-auto mb-4 text-white/50 hover:text-white transition-colors tracking-widest" data-testid="footer-terms-link">SYARAT & KETENTUAN</button>
        )}
        © {new Date().getFullYear()} ALLIED RUN · ALL RIGHTS RESERVED
      </footer>
      {showTerms && config?.terms_text && (
        <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="terms-modal" onClick={() => setShowTerms(false)}>
          <div className="card-flat max-w-2xl w-full max-h-[80vh] overflow-y-auto p-6 md:p-8 relative" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setShowTerms(false)} className="absolute top-3 right-3 text-white/60 hover:text-[#FF3B30] transition-colors" data-testid="terms-close" aria-label="Tutup"><X size={20} /></button>
            <h3 className="font-display text-2xl mb-4">SYARAT & <span className="text-[#FF3B30]">KETENTUAN</span></h3>
            <div className="text-sm text-white/70 whitespace-pre-line leading-relaxed" data-testid="terms-content">{config.terms_text}</div>
          </div>
        </div>
      )}
    </div>
  );
}
